import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Building, Users, Calendar, CheckCircle, 
  BookOpen, Video, Wrench, ExternalLink, ArrowRight
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalilePage = () => {
  const [galileInfo, setGalileInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGalile = async () => {
      try {
        const response = await axios.get(`${API}/clients/galile`);
        setGalileInfo(response.data);
      } catch (error) {
        console.error("Error fetching Galile info:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGalile();
  }, []);

  const resourceIcons = {
    guide: <BookOpen className="w-5 h-5" />,
    video: <Video className="w-5 h-5" />,
    tool: <Wrench className="w-5 h-5" />
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="galile-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-6 mb-8"
          >
            <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center">
              <Building className="w-10 h-10 text-[#007AFF]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white">
                Espace <span className="text-[#007AFF]">Galile</span>
              </h1>
              <p className="text-white/60">Portail post-formation dédié</p>
            </div>
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : galileInfo && (
        <>
          {/* Status Card */}
          <section className="pb-16">
            <div className="max-w-7xl mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card p-8"
              >
                <div className="grid md:grid-cols-3 gap-8">
                  {/* Company Info */}
                  <div>
                    <h3 className="text-white/50 text-sm mb-3">ENTREPRISE</h3>
                    <p className="text-2xl font-bold text-white mb-2">{galileInfo.company_name}</p>
                    <p className="text-white/60 mb-2">{galileInfo.sector}</p>
                    <p className="text-white/50 text-sm">{galileInfo.description}</p>
                    <a 
                      href={galileInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#007AFF] text-sm mt-4 hover:gap-3 transition-all"
                    >
                      Visiter le site
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  {/* Training Status */}
                  <div>
                    <h3 className="text-white/50 text-sm mb-3">FORMATION</h3>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          galileInfo.training_status === "En cours" 
                            ? "bg-yellow-500/20" 
                            : "bg-green-500/20"
                        }`}>
                          {galileInfo.training_status === "En cours" ? (
                            <Calendar className="w-5 h-5 text-yellow-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">Statut</p>
                          <p className={`text-sm ${
                            galileInfo.training_status === "En cours" 
                              ? "text-yellow-500" 
                              : "text-green-500"
                          }`}>
                            {galileInfo.training_status}
                          </p>
                        </div>
                      </div>

                      {galileInfo.comex_trained && (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-green-500" />
                          </div>
                          <div>
                            <p className="text-white font-medium">COMEX</p>
                            <p className="text-green-500 text-sm">Formé</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div>
                    <h3 className="text-white/50 text-sm mb-3">PROCHAINE ÉTAPE</h3>
                    <div className="p-4 bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-xl">
                      <p className="text-white font-medium mb-1">Session à planifier</p>
                      <p className="text-white/60 text-sm mb-4">
                        Une dernière session COMEX est prévue pour finaliser l'intégration.
                      </p>
                      <a 
                        href="mailto:contact@academie-ia.fr"
                        className="inline-flex items-center gap-2 text-[#007AFF] text-sm font-medium"
                      >
                        Planifier maintenant
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Resources */}
          <section className="pb-24">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-bold text-white mb-8">Ressources disponibles</h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {galileInfo.resources.map((resource, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className={`glass-card p-6 ${
                      resource.available 
                        ? 'glass-card-hover cursor-pointer' 
                        : 'opacity-50'
                    }`}
                    data-testid={`resource-${index}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      resource.available ? 'bg-[#007AFF]/10' : 'bg-white/5'
                    }`}>
                      {resourceIcons[resource.type]}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{resource.title}</h3>
                    <p className={`text-sm ${resource.available ? 'text-green-500' : 'text-white/40'}`}>
                      {resource.available ? 'Disponible' : 'Bientôt disponible'}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Interactive Game Access */}
          <section className="pb-24">
            <div className="max-w-7xl mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card p-8 md:p-12 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#007AFF]/10 to-transparent" />
                <div className="relative">
                  <h2 className="text-3xl font-bold text-white mb-4">
                    Accédez au jeu interactif
                  </h2>
                  <p className="text-white/60 max-w-xl mb-6">
                    Vos équipes peuvent découvrir quelles applications IA 
                    seraient les plus utiles pour leur poste via notre jeu interactif.
                  </p>
                  <a
                    href="/jeu/GALILE01"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="game-access"
                  >
                    Lancer le jeu
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default GalilePage;
